import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { getCustomers, createCustomer, deleteCustomer } from '../services/api';
import { toast } from 'sonner';
import { 
  Users, 
  Plus, 
  Search, 
  FolderOpen,
  Trash2,
  ArrowRight,
  Loader2,
  Building2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const CustomersPage = () => {
  const { canCreate, canDelete } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    contact_email: '',
    tags: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await getCustomers();
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const tags = formData.tags.split(',').map(t => t.trim()).filter(Boolean);
      await createCustomer({ ...formData, tags });
      toast.success('Customer created successfully');
      setShowCreateDialog(false);
      setFormData({ name: '', description: '', contact_email: '', tags: '' });
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete customer "${name}" and all related projects?`)) return;
    try {
      await deleteCustomer(id);
      toast.success('Customer deleted');
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    }
  };

  const filteredCustomers = customers.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-[#2B8FD8]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']" data-testid="customers-title">
              Customers
            </h1>
            <p className="text-slate-500 mt-1">Manage your customer accounts</p>
          </div>
          {canCreate && (
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-[#2B8FD8] hover:bg-[#2075b3]" data-testid="add-customer-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Customer
                </Button>
              </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-['Plus_Jakarta_Sans']">Add New Customer</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Customer Name *</Label>
                  <Input
                    id="name"
                    data-testid="customer-name-input"
                    placeholder="Acme Corporation"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    data-testid="customer-description-input"
                    placeholder="Brief description of the customer..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Contact Email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="customer-email-input"
                    placeholder="contact@acme.com"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tags">Tags (comma-separated)</Label>
                  <Input
                    id="tags"
                    data-testid="customer-tags-input"
                    placeholder="manufacturing, sap, b1if"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-[#2B8FD8] hover:bg-[#2075b3]"
                    disabled={creating}
                    data-testid="create-customer-submit"
                  >
                    {creating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    Create Customer
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search customers..."
            className="pl-10 max-w-md"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-customers-input"
          />
        </div>

        {/* Customer Grid */}
        {filteredCustomers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCustomers.map((customer) => (
              <Card 
                key={customer.id} 
                className="border-slate-200 hover:shadow-md transition-all hover:border-blue-200 group"
                data-testid={`customer-card-${customer.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <Building2 className="h-5 w-5 text-[#2B8FD8]" />
                      </div>
                      <div>
                        <CardTitle className="text-lg font-semibold text-slate-900 font-['Plus_Jakarta_Sans']">
                          {customer.name}
                        </CardTitle>
                        <p className="text-sm text-slate-500">{customer.project_count} projects</p>
                      </div>
                    </div>
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500"
                        onClick={(e) => { e.preventDefault(); handleDelete(customer.id, customer.name); }}
                        data-testid={`delete-customer-${customer.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {customer.description && (
                    <p className="text-sm text-slate-600 mb-3 line-clamp-2">{customer.description}</p>
                  )}
                  {customer.tags && customer.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {customer.tags.slice(0, 3).map((tag, idx) => (
                        <Badge key={idx} variant="secondary" className="bg-slate-100 text-slate-600">
                          {tag}
                        </Badge>
                      ))}
                      {customer.tags.length > 3 && (
                        <Badge variant="secondary" className="bg-slate-100 text-slate-600">
                          +{customer.tags.length - 3}
                        </Badge>
                      )}
                    </div>
                  )}
                  <Link to={`/customers/${customer.id}`}>
                    <Button variant="outline" size="sm" className="w-full group-hover:border-[#2B8FD8] group-hover:text-[#2B8FD8]">
                      View Details <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-slate-200 border-dashed">
            <CardContent className="py-16 text-center">
              <Users className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2 font-['Plus_Jakarta_Sans']">
                {searchQuery ? 'No customers found' : 'No customers yet'}
              </h3>
              <p className="text-slate-500 mb-6 max-w-sm mx-auto">
                {searchQuery 
                  ? 'Try adjusting your search terms' 
                  : 'Get started by adding your first customer to organize your integration documentation'}
              </p>
              {!searchQuery && (
                <Button 
                  className="bg-[#2B8FD8] hover:bg-[#2075b3]"
                  onClick={() => setShowCreateDialog(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Customer
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default CustomersPage;
